import { ReactNode, createContext, useContext } from 'react'
import { useRevalidator, useRouteError, useRouteLoaderData } from 'react-router-dom'
import { Me, getMe } from '../api/me'
import { Button } from '../components/common'
import { ApiError } from '../lib/api'

export const ME_ROUTE_ID = 'app'

const MeContext = createContext<{ readonly me: Me; readonly reload: () => void } | null>(null)

const RATE_LIMITED = 'Demasiadas solicitudes en poco tiempo. Espera unos minutos y vuelve a intentarlo.'
const UNAVAILABLE = 'No pudimos cargar tu sesión. Revisa tu conexión e inténtalo nuevamente.'

let loadedMe: Me | null = null

/**
 * Runs with every navigation, so the navbar switches in the same commit as the page. An
 * unchanged answer keeps the previous object, so effects keyed on `me` do not rerun, and a
 * failed refresh keeps the session already on screen rather than replacing the app.
 */
export async function loadMe(): Promise<Me> {
  try {
    const me = await getMe()
    loadedMe = loadedMe && JSON.stringify(me) === JSON.stringify(loadedMe) ? loadedMe : me
    return loadedMe
  } catch (error) {
    if (loadedMe) {
      return loadedMe
    }
    throw error
  }
}

export function MeLoading() {
  return (
    <div className="text-center" style={{ marginTop: '20vh', color: '#bbb' }}>
      <i className="fa fa-spinner fa-spin fa-3x" />
    </div>
  )
}

export function MeLoadError() {
  const error = useRouteError()
  const { revalidate } = useRevalidator()
  return (
    <div className="text-center" style={{ marginTop: '20vh' }} data-testid="me-error">
      <p style={{ color: '#777' }}>{error instanceof ApiError && error.status === 429 ? RATE_LIMITED : UNAVAILABLE}</p>
      <Button onClick={revalidate}>Reintentar</Button>
    </div>
  )
}

export function MeProvider({ children }: { readonly children: ReactNode }) {
  const me = useRouteLoaderData(ME_ROUTE_ID) as Me
  const { revalidate } = useRevalidator()
  return <MeContext.Provider value={{ me, reload: revalidate }}>{children}</MeContext.Provider>
}

export function useMe(): Me {
  const context = useContext(MeContext)
  if (!context) {
    throw new Error('useMe must be used within MeProvider')
  }
  return context.me
}

export function useMeReload(): () => void {
  const context = useContext(MeContext)
  if (!context) {
    throw new Error('useMeReload must be used within MeProvider')
  }
  return context.reload
}
